import { ERROR_MESSAGE, TIME_OUT, TOKEN, OPENID_KEY } from '@/constants';
import localConf from '@/local-conf';
import { useAppStore } from '@/store';
import { FetchAbnormalCodes } from '@/types';
import { businessUtils, storage, toast, commonUtils } from '@/utils';
import errorManager, { ErrorManagerEntryType } from '@/utils/errorManager';
import Request, {
  type HttpRequestConfig,
  type HttpRequestHeader,
  type HttpTask,
} from 'luch-request';
import { getLoginExternal } from '../login/wechatMp';
import wechatH5 from '@/services/login/wechatH5';

const channelToken = localConf.channel;

const getToken = (token: string = '') => {
  const t = token || storage.get(TOKEN);
  return `bearer ${t}`;
};

// 是否正在刷新的标记
let isRefreshing = false;

// 重试队列，每一项将是一个待执行的函数形式
let retryRequests: ((token: any) => void)[] = [];

// 创建请求实例方法
function createRequest(options = {}) {
  return new Request({
    timeout: TIME_OUT,
    ...options,
  });
}

const baseURL: string = localConf.host + '/shop-api';

// 创建http实例对象
const clientHttp = createRequest({
  baseURL,
});

// 请求拦截
clientHttp.interceptors.request.use(
  (config) => {
    config.header = {
      Authorization: getToken(),
      'vendure-token': channelToken,
    };
    return config;
  },
  (config) => {
    return Promise.reject(config);
  }
);

/** 异常码 */
const abnormalCodes = [
  // 优惠券礼包错误
  FetchAbnormalCodes.COUPON_BUNDLE_RECEIVE_ERROR,
  // 操作错误
  FetchAbnormalCodes.OPERATION_ERROR,
  // 限购组错误
  FetchAbnormalCodes.EXCLUSION_GROUP_ERROR,
  // 积分不足
  FetchAbnormalCodes.POINTS_NOT_ENOUGH_ERROR,
  // 积分活动已失效
  FetchAbnormalCodes.POINTS_PROMOTION_EXPIRED_ERROR,
  // 积分商品不能直接购买
  FetchAbnormalCodes.POINTS_PRODUCT_CANNOT_BUY_ERROR,
  // 积分订单只能存在一个商品
  FetchAbnormalCodes.POINTS_ONLY_ONE_PER_ORDER_ERROR,
  // 商品收货地址不支持配送
  FetchAbnormalCodes.PRODUCT_REGION_RESTRICTION_ERROR,
];

// 响应拦截
clientHttp.interceptors.response.use(
  (res) => {
    const err = res.data?.errors?.[0];
    const abnormalCode = err?.extensions?.code;

    if (abnormalCodes.includes(abnormalCode)) {
      toast.clear();
      // 处理全局错误提示
      if (abnormalCode === FetchAbnormalCodes.OPERATION_ERROR) {
        errorManager.report(ErrorManagerEntryType.requestApi, {
          message: err?.message ?? '',
          requestInfo: {
            url: err?.path,
            params: res?.config?.data?.variables,
            statusCode: res?.statusCode,
            responseData: res?.data,
          },
        });
        setTimeout(() => toast.message(err?.message ?? ERROR_MESSAGE.system), 200);
        return;
      }
      return Promise.reject({
        err,
        abnormalCode,
      });
    }

    if (abnormalCode === 'UNAUTHORIZED') {
      toast.clear();
      return refreshToken(res.config);
    }

    // #ifdef H5
    if (err?.message === "Unauthorized user" && commonUtils.mp.isH5()) {
      toast.clear();
      storage.remove(OPENID_KEY);
      return toast.message("请重新登录", () => wechatH5());
    }
    // #endif

    if (err) {
      toast.clear();
      setTimeout(() => toast.message(ERROR_MESSAGE.system), 200);
      console.log(`接口错误信息：`, {
        message: err?.message,
        url: err?.path,
        params: res?.config?.data?.variables,
        statusCode: res?.statusCode,
        responseData: res?.data,
      });
    }

    // console.log(`clientHttp.interceptors.response`, res.data);

    return res.data;
  },
  // 错误处理
  (err) => {
    console.log(`<clientHttp Error>`, {
      err,
      code: err.statusCode,
      message: err.errMsg,
    });

    // 处理请求超时
    if (err.errMsg === 'request:fail timeout') {
      const appStore = useAppStore();
      if (!appStore.networkErrorFlag) {
        appStore.networkErrorFlag = true;
      }
    }

    toast.clear();
    toast.message(ERROR_MESSAGE.network);

    errorManager.report(ErrorManagerEntryType.requestApi, {
      message: err.errMsg,
      requestInfo: {
        url: err.err.config.baseURL,
        params: err.err.config.data,
        statusCode: err.statusCode,
        responseData: err.err.data,
      },
    });

    return Promise.reject(err);
  }
);

const refreshToken = async (config: HttpRequestConfig<HttpTask>) => {
  if (!isRefreshing) {
    isRefreshing = true;

    // #ifdef MP-WEIXIN
    return getLoginExternal()
      .then(async (res) => {
        const token = res?.token ?? '';
        if (!token) {
          console.log(`<refresh-token不存在>`, token);
          storage.remove(TOKEN);
          await businessUtils.login.exection();
          return;
        }

        // 重新设置token
        storage.set(TOKEN, token, 24);

        // 不需要baseURL是因为会重新请求url，url中已经包含baseURL的部分了
        config.baseURL = '';
        (config.header as HttpRequestHeader)['Authorization'] = getToken(token);
        const data = clientHttp.request(config);

        // 将所有队列中的请求进行重试
        retryRequests.forEach((cb) => cb(token));
        // 重试完清空这个队列
        retryRequests = [];

        return data;
      })
      .catch((err) => console.log(`<refresh-token失败，请检查错误>`, err))
      .finally(() => (isRefreshing = false));
    // #endif
  } else {
    // 正在刷新token，返回一个未执行resolve的promise
    return new Promise((resolve) => {
      // 将resolve放进队列，用一个函数形式来保存，等token刷新后直接执行
      retryRequests.push((token: string) => {
        config.baseURL = '';
        (config.header as HttpRequestHeader)['Authorization'] = getToken(token);
        resolve(clientHttp.request(config));
      });
    });
  }
};

export default clientHttp;
